// Starter-orchestrator scaffold — the ready-to-run conversation flow a NEW bot
// begins with, generated per vertical. PURE (no React, no DB): shared so the web
// editor can render/regenerate it AND the API can auto-seed it on bot creation.
// Produces a loose flow shape (StarterFlow); the runtime validates it at publish
// and the web treats it as its RawFlow (identical structure).
//
// WHAT THIS FILE IS NOW (Process-Compiled Agent, Wave 2). The EMITTER moved to
// `../compiler/`: it stopped being a per-vertical generator and became the
// process compiler, which consumes a `CompilerInput` (the tenant's connected
// processes + their stage behaviors + their fichas) instead of the literal
// category lists hardcoded below. What stays here is what was always
// vertical-specific: the per-industry category literals, and the two entry
// points the rest of the codebase imports.
//
// `buildVerticalFlow` survives as a THIN WRAPPER over `compileChannelAgent`, and
// that is deliberate: every existing consumer (seed-orchestrator.ts, the
// validator's factory pin, the demo-seeder drift test) keeps calling exactly
// what it called before and keeps getting byte-identical output. If the
// de-hardcoding had changed one node, those tests go red — which is the point.
//
// The moved declarations are re-exported below under their original names, so
// `@vysto/shared/bot-config/orchestrator-scaffold` has the exact same surface it
// had before the split and no consumer's import changed.

import { CAPTURE_CATEGORY_KEY, CAPTURE_CATEGORY_LABEL } from '../compiler/capture-flow.js'
import { compileChannelAgent } from '../compiler/compile.js'
import type { StarterFlow } from '../compiler/flow.js'
import type { CompilerCategory } from '../compiler/types.js'

// ── Re-exports: the surface this module has always had ─────────────────────

export {
  buildCaptureFlow,
  CAPTURE_CATEGORY_KEY,
  CAPTURE_CATEGORY_LABEL,
  captureCategoryLabelFor,
  CAPTURE_FLOW_FUNNEL,
  CAPTURE_FLOW_INTENT,
  CAPTURE_FLOW_NAME,
  CAPTURE_FLOW_STAGE,
  GENERATED_CAPTURE_CONFIRM,
  HEALTHCARE_SPECIALTIES,
  isGeneratedCaptureConfirm,
  processNameInCaptureCategoryLabel,
  processNameInCaptureFlowName,
} from '../compiler/capture-flow.js'
export type {
  CaptureFlowOptions,
  CaptureFlowQuestion,
  PreservedAskConfig,
} from '../compiler/capture-flow.js'
export { DEFAULT_BRANCH, whenForIntent } from '../compiler/flow.js'
export type { StarterEdge, StarterFlow, StarterNode, StarterNodeType } from '../compiler/flow.js'

/** A clean, guided starter orchestrator that behaves like a real conversation:
 *  greet once, then loop — discover what the customer wants, act on it, and come
 *  back to listen for the next message, until they're done or ask for a person.
 *
 *  The loop is the whole point (Juan's "loop until something happens"):
 *    start → discover → (responder | pasar con asesor | fin) ; responder + asesor
 *    both loop BACK to discover so the agent keeps serving message after message.
 *
 *  Descubrir ships with three example categories in plain language the owner
 *  edits to their business — so it's never locked to one vertical. The safety net
 *  (Ajustes) still transfers to a person automatically if the agent gets stuck. */
export function buildGuidedOrchestrator(greeting: string): StarterFlow {
  return {
    nodes: [
      { id: 'start', type: 'msg', position: { x: 260, y: 20 }, config: { text: greeting } },
      {
        id: 'discover',
        type: 'route',
        position: { x: 260, y: 160 },
        config: {
          var: 'intent',
          options: [
            { key: 'pregunta', label: 'Tiene una pregunta o duda' },
            { key: 'asesor', label: 'Quiere hablar con una persona' },
            { key: 'despedida', label: 'Se despide o ya terminó' },
          ],
        },
      },
      { id: 'responder', type: 'answer', position: { x: 40, y: 320 }, config: {} },
      {
        id: 'pasar_asesor',
        type: 'handoff',
        position: { x: 260, y: 320 },
        config: { reason: 'El cliente pidió hablar con una persona' },
      },
      { id: 'fin_ok', type: 'fin', position: { x: 480, y: 320 }, config: { kind: 'ok' } },
    ],
    edges: [
      { from: 'start', to: 'discover' },
      { from: 'discover', to: 'responder', when: 'vars.intent == "pregunta"' },
      { from: 'discover', to: 'pasar_asesor', when: 'vars.intent == "asesor"' },
      { from: 'discover', to: 'fin_ok', when: 'vars.intent == "despedida"' },
      // Anything else: answer from the knowledge base.
      { from: 'discover', to: 'responder', when: 'true' },
      // Loop back to keep the conversation going after each turn.
      { from: 'responder', to: 'discover' },
      { from: 'pasar_asesor', to: 'discover' },
    ],
  }
}

/** Assemble a valid looping orchestrator from a vertical's category spec:
 *  greet → discover → (answer | handoff | end | capture→handoff) → loop. Reuses
 *  shared Responder / handoff / Fin nodes; each `capture` adds a `capturar_lead`
 *  act node that uses the contact's WhatsApp name (ambient `${contact.*}`), so no
 *  extra question is needed. Always loops back to discover; always reachable
 *  fin.ok (the `despedida` category).
 *
 *  THIN WRAPPER (Wave 2). The emitter it used to BE now lives in
 *  `../compiler/compile.ts` and reads a `CompilerInput`; this signature is the
 *  CompilerInput whose `processes` list is empty and whose `copy` is the
 *  defaults. Output is byte-identical, which is what the untouched scaffold
 *  tests assert. */
export function buildVerticalFlow(
  greeting: string,
  handoffReason: string,
  cats: CompilerCategory[],
): StarterFlow {
  return compileChannelAgent({ greeting, handoffReason, categories: cats }).orchestrator
}

/** A vertical-shaped starter orchestrator (M7). Picks Descubrir categories +
 *  routing that fit the bot's industry, so a new bot starts working like that
 *  business instead of a blank loop. Falls back to the generic skeleton. The
 *  owner edits everything afterward. */
export interface StarterOrchestratorOptions {
  /** Id of the ALREADY-PUBLISHED capture task flow. When present, the clinic
   *  starter gains a Descubrir category whose branch `call`s it, which is the
   *  only way a task flow is reachable on a bot that has an orchestrator: the
   *  pipeline hands an orchestrator bot's whole turn to the orchestrator and
   *  never reaches intent dispatch (process-inbound.ts, step 4c). Absent — the
   *  web editor's "Crear flujo principal", which publishes no task flow — the
   *  category is omitted rather than pointing at a `call` with no target. */
  captureSkillId?: string
}

/** EL GIRO, EN DATOS: la razón del traspaso y las categorías base del Descubrir.
 *
 *  Se EXTRAJO del `switch` de `buildStarterOrchestrator` sin tocar un literal.
 *  Existe porque la vista previa compilada tiene que compilar EXACTAMENTE las
 *  mismas opciones base que el agente publicado ya lleva: sin ellas el lado
 *  compilado traía dos opciones donde el vivo trae nueve, y un `Actualizar` de
 *  la ola 4 habría borrado las siete restantes sin decir una palabra (F2-1).
 *  Una sola fuente, dos consumidores. */
export interface VerticalPreset {
  handoffReason: string
  categories: CompilerCategory[]
}

/** El preset del giro, o `null` cuando no hay ninguno y se cae al esqueleto
 *  genérico (`buildGuidedOrchestrator`, que no tiene categorías declaradas). */
export function verticalPresetOf(
  vertical: string | undefined,
  options: StarterOrchestratorOptions = {},
): VerticalPreset | null {
  switch (vertical) {
    case 'automotive-new':
    case 'automotive-used':
      return {
        handoffReason: 'Pasar con un vendedor',
        categories: [
          {
            key: 'comprar',
            label: 'Quiere comprar un auto',
            action: {
              kind: 'book',
              interes: 'Comprar un auto',
              confirm:
                'Gracias. Un asesor de ventas te contactará por aquí para darte opciones y agendar una prueba de manejo.',
              asks: [
                // El modelo ES el interés de la agencia: su preset se llama
                // 'Modelo de interés' y vive en la columna. Antes esta respuesta
                // se guardaba en `campos.modelo_interes` (clave que el catálogo ya
                // no tiene) mientras la columna recibía la constante 'Comprar un
                // auto', así que cada tarjeta del tablero decía lo mismo.
                {
                  var: 'modelo_interes',
                  prompt: '¿Qué modelo o tipo de auto te interesa?',
                  interest: true,
                },
                {
                  var: 'presupuesto',
                  prompt:
                    '¿Tienes un presupuesto aproximado en mente? (puedes decir "no estoy seguro")',
                },
              ],
            },
          },
          {
            key: 'cotizar',
            label: 'Quiere una cotización o un precio',
            action: { kind: 'answer' },
          },
          { key: 'servicio', label: 'Servicio, garantía o postventa', action: { kind: 'answer' } },
          { key: 'vendedor', label: 'Quiere hablar con un vendedor', action: { kind: 'handoff' } },
          { key: 'despedida', label: 'Se despide o ya terminó', action: { kind: 'end' } },
        ],
      }
    // Solo doctors ('doctor') share the clinic template — the booking engine
    // already handles a single-provider practice; the specialty ask just resolves
    // to that one doctor.
    case 'doctor':
    case 'healthcare':
      return {
        handoffReason: 'Pasar con un coordinador',
        categories: [
          {
            key: 'cita',
            label:
              'Quiere agendar una cita NUEVA, o ver o consultar a un doctor o especialista por primera vez',
            action: {
              kind: 'schedule',
              specialtyPrompt: '¿Para qué especialidad o servicio quieres la cita?',
              datePrompt:
                'Con gusto, una cita de ${especialidad}. ¿Qué día y horario te quedarían mejor?',
              noSlots:
                'Por ahora no tengo horarios disponibles para eso. Un coordinador te ayudará a encontrar un espacio por aquí en breve.',
              notOffered:
                'Déjame verificar esa especialidad con el equipo. En un momento te comunico con un coordinador que te confirma la disponibilidad.',
            },
          },
          {
            // Real self-service reschedule/cancel: find the patient's upcoming cita
            // (by their WhatsApp phone, else a confirmation number), read it back,
            // then cancel it or reschedule (cancel + rebook via the booking branch).
            key: 'cambiar',
            label: 'Quiere cambiar, reagendar o cancelar una cita que YA tiene agendada',
            action: {
              kind: 'manage',
              folioPrompt:
                'Para encontrar tu cita, ¿me compartes tu número de confirmación? ' +
                'Es el que te dimos cuando agendaste.',
              notFound:
                'No encontré ninguna cita a tu nombre. Te paso con una persona del equipo ' +
                'para revisarlo.',
              bookEntry: 'pedir_cita_especialidad',
            },
          },
          {
            key: 'cotizar',
            label: 'Pregunta el precio o costo de un estudio, análisis o servicio específico',
            action: {
              kind: 'quote',
              estudioPrompt: '¿Qué estudio o servicio te gustaría cotizar?',
              notFound:
                'Déjame confirmarte el costo exacto de eso. En un momento te comunico con un coordinador.',
              bookEntry: 'pedir_cita_especialidad',
            },
          },
          // Only when the capture task flow already exists on this bot. Sits
          // AFTER cita/cambiar/cotizar so a customer who wants a real cita gets
          // the booking branch: this one is the person who is not ready to book
          // and just wants to be called back.
          ...(options.captureSkillId
            ? [
                {
                  key: CAPTURE_CATEGORY_KEY,
                  label: CAPTURE_CATEGORY_LABEL,
                  action: { kind: 'call' as const, skillId: options.captureSkillId },
                },
              ]
            : []),
          {
            key: 'pregunta',
            label: 'Hace una pregunta general sobre médicos, ubicación, horarios o cómo llegar',
            action: { kind: 'answer' },
          },
          {
            key: 'urgencia',
            label: 'Describe una emergencia médica o síntomas graves',
            action: { kind: 'handoff' },
          },
          {
            key: 'persona',
            label: 'Pide explícitamente hablar con una persona, recepción o un humano',
            action: { kind: 'handoff' },
          },
          { key: 'despedida', label: 'Se despide, agradece o ya terminó', action: { kind: 'end' } },
        ],
      }
    case 'casas-de-empeno':
      return {
        handoffReason: 'Pasar con un asesor',
        categories: [
          {
            key: 'avaluo',
            label: 'Quiere un avalúo o empeñar algo',
            action: {
              kind: 'book',
              interes: 'Avalúo / empeño',
              confirm: 'Gracias. Un asesor te dará el avalúo y las condiciones por aquí en breve.',
              asks: [
                // El artículo ES el interés de la casa de empeño: su preset se
                // llama 'Artículo' y vive en la columna. Antes iba a
                // `campos.articulo` (clave borrada del catálogo) y la columna
                // recibía la constante 'Avalúo / empeño'.
                {
                  var: 'articulo',
                  prompt: '¿Qué artículo te gustaría empeñar o avaluar?',
                  interest: true,
                },
                {
                  var: 'monto_buscado',
                  prompt: '¿Qué cantidad aproximada necesitas? (puedes decir "no estoy seguro")',
                },
              ],
            },
          },
          { key: 'refrendo', label: 'Refrendo o pago de un préstamo', action: { kind: 'answer' } },
          { key: 'pregunta', label: 'Pregunta sobre el servicio', action: { kind: 'answer' } },
          { key: 'asesor', label: 'Quiere hablar con una persona', action: { kind: 'handoff' } },
          { key: 'despedida', label: 'Se despide o ya terminó', action: { kind: 'end' } },
        ],
      }
    default:
      return null
  }
}

/** El orquestador de arranque de un giro: el preset de arriba, compilado. Sigue
 *  siendo la única entrada que el sembrado y el editor llaman, y sigue
 *  devolviendo lo mismo byte por byte. */
export function buildStarterOrchestrator(
  vertical: string | undefined,
  greeting: string,
  options: StarterOrchestratorOptions = {},
): StarterFlow {
  const preset = verticalPresetOf(vertical, options)
  if (preset === null) return buildGuidedOrchestrator(greeting)
  return buildVerticalFlow(greeting, preset.handoffReason, preset.categories)
}
